"use client";

import { cn } from "@/lib/utils/general-utils";
import { Eye as EyeIcon, EyeOff as EyeSlashIcon } from "lucide-react";
import { forwardRef, useState } from "react";

export interface PasswordInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  | "type"
  | "value"
  | "defaultValue"
  | "onChange"
  | "onInput"
  | "onBlur"
  | "onFocus"
> {
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Marks the input as invalid (applies red border). */
  invalid?: boolean;
  /** Whether to show the password visibility toggle (default: true) */
  showPasswordToggle?: boolean;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    { invalid, showPasswordToggle = true, disabled, className, ...props },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputClasses = cn(
      "relative block w-full appearance-none rounded-lg bg-white",
      "border border-input hover:border-ring transition-colors",
      "py-1.5 px-3 text-sm text-foreground placeholder:text-muted-foreground",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
      "disabled:cursor-not-allowed disabled:opacity-50",
      invalid && "border-destructive hover:border-destructive",
      showPasswordToggle && "pr-10 sm:pr-9",
    );

    if (!showPasswordToggle) {
      return (
        <span data-slot="control" className="relative block w-full">
          <input
            ref={ref}
            type="password"
            disabled={disabled}
            className={cn(inputClasses, className)}
            {...props}
          />
        </span>
      );
    }

    return (
      <span
        data-slot="control"
        className={cn("relative block w-full", className)}
      >
        <input
          ref={ref}
          type={showPassword ? "text" : "password"}
          disabled={disabled}
          className={inputClasses}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          disabled={disabled}
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2 z-10",
            "size-5 sm:size-4",
            "text-muted-foreground hover:text-foreground",
            "transition-colors duration-200",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
          aria-label={showPassword ? "Hide password" : "Show password"}
          aria-pressed={showPassword}
        >
          {showPassword ? (
            <EyeSlashIcon className="w-full h-full" />
          ) : (
            <EyeIcon className="w-full h-full" />
          )}
        </button>
      </span>
    );
  },
);

PasswordInput.displayName = "PasswordInput";
